'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import dynamic from 'next/dynamic'

const Logo3D = dynamic(() => import('./Logo3D'), {
  ssr: false,
  loading: () => <div className="logo-3d-container" aria-hidden="true" />,
})

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [burgerMenuOffset, setBurgerMenuOffset] = useState<number | null>(null)
  const [isMobile, setIsMobile] = useState(false)
  const pathname = usePathname()
  const isHomePage = pathname === '/'

  // Calculate and match burger menu offset to logo offset
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    const calculateOffset = () => {
      if (window.innerWidth > 768) {
        setBurgerMenuOffset(null)
        return // Only on mobile
      }

      const logoElement = document.querySelector('.logo-3d-container') as HTMLElement
      const navElement = document.querySelector('.header-nav') as HTMLElement

      if (logoElement && navElement) {
        const logoRect = logoElement.getBoundingClientRect()

        // Calculate logo's offset from viewport left edge
        const logoLeftOffset = logoRect.left

        // Calculate nav's right padding (should match left padding of 1rem = 16px)
        const navStyles = window.getComputedStyle(navElement)
        const navRightPadding = parseFloat(navStyles.paddingRight)

        // Calculate additional margin needed for burger menu to match logo's visual offset
        // If logo is X pixels from left, burger should be X pixels from right
        // Nav already has right padding, so we need: logoLeftOffset - navRightPadding
        const additionalMargin = logoLeftOffset - navRightPadding

        // Apply the calculated offset (only if positive, otherwise use 0)
        setBurgerMenuOffset(Math.max(0, additionalMargin))
      }
    }

    // Calculate on mount and resize
    calculateOffset()
    window.addEventListener('resize', calculateOffset)

    // Also calculate after a short delay to ensure logo is rendered
    const timeout = setTimeout(calculateOffset, 100)

    return () => {
      window.removeEventListener('resize', checkMobile)
      window.removeEventListener('resize', calculateOffset)
      clearTimeout(timeout)
    }
  }, [])

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    // Only handle smooth scroll on homepage
    if (!isHomePage) return

    e.preventDefault()
    const element = document.querySelector(href) as HTMLElement
    if (element) {
      const headerOffset = 80
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.scrollY - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
      // Close mobile menu after clicking a link
      setIsMobileMenuOpen(false)
    }
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  // Navigation items with both hash (for homepage) and page URLs.
  // `external` points to a full URL on another domain (opens in a new tab).
  const navItems = [
    { label: 'Services', hash: '#services', page: '/services' },
    { label: 'Products', hash: '#products', page: '/products' },
    { label: 'About', hash: '#about', page: '/about' },
    { label: 'FAQ', hash: '#faq', page: '/faq' },
    { label: 'Careers', external: 'https://careers.ne-room.com' },
  ]

  return (
    <header className="header-container">
      <nav className="container header-nav">
        <Link href="/" className="logo" aria-label="NEROOM home">
          {/* 3D Logo with animation on hover */}
          <Logo3D />
        </Link>
        <input type="checkbox" id="mobile-menu-toggle" className="mobile-menu-toggle" checked={isMobileMenuOpen} onChange={toggleMobileMenu} />
        <label htmlFor="mobile-menu-toggle" className="mobile-menu-icon" style={burgerMenuOffset !== null && isMobile ? { marginRight: `${burgerMenuOffset}px` } : {}}>
          <span></span>
          <span></span>
          <span></span>
        </label>
        <ul className={`nav-menu ${isMobileMenuOpen ? 'active' : ''}`}>
          {navItems.map((item) => (
            <li key={item.label}>
              {item.external ? (
                <a
                  href={item.external}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nav-link"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </a>
              ) : isHomePage ? (
                <a href={item.hash} onClick={(e) => handleLinkClick(e, item.hash!)} className="nav-link">
                  {item.label}
                </a>
              ) : (
                <Link href={item.page!} className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>
                  {item.label}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </header>
  )
}
